import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { FaRegPenToSquare } from 'react-icons/fa6';
import Flatpickr from 'react-flatpickr';
import { Thai } from 'flatpickr/dist/l10n/th';

import { RootState } from 'slices';
import {
  setActiveBreakingTimeAttendanceId,
  setBreakingTimeModalOpen,
  setBreakingTimeValues,
} from 'slices/timeAttendance/reducer';
import {
  RESPONSE_MESSAGE,
  CalendarFormats,
  TimeAttendanceListActionButtons,
} from 'pages/TimeAttendance/constants';
import { EditBreakingTimeRowType } from 'types/timeAttendance/timeAttendanceTypes';
import {
  mapApiToBreakingTimeRow,
  mapEditBreakingTimeToRequest,
} from 'utils/mappers/timeAttendanceMapper';
import { putUpdateBreakingTime } from 'services/timeAttendanceService';
import IconActionButton from 'Components/pos2u/Buttons/IconActionButton';
import { validationSchema } from './validationSchema';
import { hoursToHHMM } from 'utils/common/Time';

type BreakingTimeRowErrors = Partial<
  Record<'breakStartAt' | 'breakEndAt' | 'note', string>
>;

const BreakingTimeModal = ({
  fetchTimeAttendance,
}: {
  fetchTimeAttendance: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    BreakingTimeModalOpen,
    BreakingTimeValues,
    activeBreakingTimeAttendanceId,
  } = useSelector((state: RootState) => state.TimeAttendance);

  const [draftRows, setDraftRows] = useState<EditBreakingTimeRowType[]>([]);
  const [originalRows, setOriginalRows] = useState<EditBreakingTimeRowType[]>(
    []
  );
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [savingRowId, setSavingRowId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<
    Record<string, BreakingTimeRowErrors>
  >({});

  const isDisplayEmptyDate = useCallback((value: Date | null) => {
    if (!value) return true;
    return !dayjs(value).isValid();
  }, []);

  const formatDisplayDate = useCallback(
    (value: Date | null): string => {
      if (isDisplayEmptyDate(value)) return '';
      return dayjs(value).format('DD/MM/YYYY HH:mm:ss');
    },
    [isDisplayEmptyDate]
  );

  const formatDisplayValue = useCallback((value?: string | null): string => {
    if (!value || value.trim() === '' || value === 'null') return '';
    return value;
  }, []);

  useEffect(() => {
    setDraftRows(BreakingTimeValues);
    setOriginalRows(BreakingTimeValues);
    setEditingRowId(null);
    setRowErrors({});
  }, [BreakingTimeValues]);

  const closeModal = useCallback(() => {
    dispatch(setBreakingTimeModalOpen(false));
    dispatch(setActiveBreakingTimeAttendanceId(null));
    setEditingRowId(null);
    setSavingRowId(null);
    setRowErrors({});
  }, [dispatch]);

  const getLocale = useCallback(() => {
    switch (i18n.language) {
      case 'th':
        return Thai;
      default:
        return undefined;
    }
  }, [i18n.language]);
  const minSelectableDate = useMemo(() => {
    const today = new Date();
    const minDate = new Date(today);

    minDate.setFullYear(today.getFullYear() - 3);
    minDate.setHours(0, 0, 0, 0);

    return minDate;
  }, []);
  const maxSelectableDate = useMemo(() => {
    const today = new Date();

    today.setHours(23, 59, 59, 999);

    return today;
  }, []);

  const rowsById = useMemo(
    () =>
      draftRows.reduce<Record<string, EditBreakingTimeRowType>>((acc, row) => {
        acc[row.id] = row;
        return acc;
      }, {}),
    [draftRows]
  );

  const handleEditClick = useCallback((id: string) => {
    setEditingRowId(id);
    setRowErrors((prev) => ({ ...prev, [id]: {} }));
  }, []);

  const handleCancelClick = useCallback(
    (id: string) => {
      const originalRow = originalRows.find((row) => row.id === id);
      if (!originalRow) return;

      setDraftRows((prevRows) =>
        prevRows.map((row) => (row.id === id ? originalRow : row))
      );
      setEditingRowId(null);
      setRowErrors((prev) => ({ ...prev, [id]: {} }));
    },
    [originalRows]
  );

  const clearRowFieldError = useCallback(
    (id: string, field: keyof BreakingTimeRowErrors) => {
      setRowErrors((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: '',
        },
      }));
    },
    []
  );

  const handleChangeField = useCallback(
    (id: string, value: string) => {
      setDraftRows((prevRows) =>
        prevRows.map((row) => {
          if (row.id !== id) return row;

          return {
            ...row,
            note: value,
          };
        })
      );
      clearRowFieldError(id, 'note');
    },
    [clearRowFieldError]
  );

  const handleDateChange = useCallback(
    (
      id: string,
      field: 'breakStartAt' | 'breakEndAt',
      date: Date | undefined
    ) => {
      setDraftRows((prevRows) =>
        prevRows.map((row) => {
          if (row.id !== id) return row;

          return {
            ...row,
            [field]: date ?? null,
          };
        })
      );
      clearRowFieldError(id, field);
    },
    [clearRowFieldError]
  );

  const validateRow = useCallback(
    async (row: EditBreakingTimeRowType) => {
      try {
        await validationSchema(t).validate(row, { abortEarly: false });
        setRowErrors((prev) => ({ ...prev, [row.id]: {} }));
        return true;
      } catch (error: any) {
        const nextErrors: BreakingTimeRowErrors = {};

        error.inner?.forEach((item: any) => {
          if (
            !item.path ||
            nextErrors[item.path as keyof BreakingTimeRowErrors]
          ) {
            return;
          }
          nextErrors[item.path as keyof BreakingTimeRowErrors] = item.message;
        });

        setRowErrors((prev) => ({
          ...prev,
          [row.id]: nextErrors,
        }));
        return false;
      }
    },
    [t]
  );

  const handleSaveClick = useCallback(
    async (id: string) => {
      const row = rowsById[id];
      if (!row || !activeBreakingTimeAttendanceId) return;

      const isValid = await validateRow(row);
      if (!isValid) {
        return;
      }

      try {
        setSavingRowId(id);

        const response = await putUpdateBreakingTime(
          id,
          mapEditBreakingTimeToRequest(row)
        );

        if (
          response &&
          response.message === 'OK' &&
          response.error_code === 0 &&
          response.data
        ) {
          const updatedRow = mapApiToBreakingTimeRow(response.data);
          const updatedRows = draftRows.map((item) =>
            item.id === id ? updatedRow : item
          );

          setDraftRows(updatedRows);
          setOriginalRows(updatedRows);
          dispatch(setBreakingTimeValues(updatedRows));
          setEditingRowId(null);
          setRowErrors((prev) => ({ ...prev, [id]: {} }));
          closeModal();
          fetchTimeAttendance();

          toast.success(RESPONSE_MESSAGE.UPDATE_SUCCESS, { autoClose: 3000 });
        } else {
          toast.error(response?.message || 'Error occurred', {
            autoClose: 3000,
          });
        }
      } catch (error: any) {
        toast.error(error.message || 'Unexpected error', { autoClose: 3000 });
      } finally {
        setSavingRowId(null);
      }
    },
    [
      activeBreakingTimeAttendanceId,
      dispatch,
      draftRows,
      rowsById,
      validateRow,
      fetchTimeAttendance,
      closeModal,
    ]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Modal
          size="xl"
          isOpen={BreakingTimeModalOpen}
          toggle={closeModal}
          backdrop="static"
          centered
        >
          <ModalHeader className="bg-light p-3" toggle={closeModal}>
            {t('Break Time List')}
          </ModalHeader>
          <ModalBody className="px-0 pt-0 pb-4">
            <div className="mt-3 table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="p-3" style={{ width: '60px' }}>
                      #
                    </th>
                    <th>{t('Break Start Time')}</th>
                    <th>{t('Break End Time')}</th>
                    <th className="text-center">{t('Breaking Hours')}</th>
                    <th>{t('Note')}</th>
                    <th className="text-center" style={{ width: '220px' }}>
                      {t('Action')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {draftRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        {t('No results found')}
                      </td>
                    </tr>
                  ) : (
                    draftRows.map((row, index) => {
                      const isEditing = editingRowId === row.id;
                      const isSaving = savingRowId === row.id;
                      const errors = rowErrors[row.id] || {};

                      return (
                        <tr
                          key={row.id}
                          className={isEditing ? 'align-top' : ''}
                        >
                          <td className="p-3">{index + 1}</td>
                          <td>
                            {isEditing ? (
                              <div
                                className="d-flex flex-column"
                                style={{ minWidth: '150px', maxWidth: '180px' }}
                              >
                                <Flatpickr
                                  className="form-control"
                                  options={{
                                    enableTime: true,
                                    dateFormat: CalendarFormats.dateTimeFormat,
                                    time_24hr: true,
                                    locale: getLocale(),
                                    minDate: minSelectableDate,
                                    maxDate: maxSelectableDate,
                                  }}
                                  value={row.breakStartAt ?? undefined}
                                  onChange={(date) =>
                                    handleDateChange(
                                      row.id,
                                      'breakStartAt',
                                      date[0]
                                    )
                                  }
                                />

                                <div
                                  className={`mt-1 ${
                                    errors.breakStartAt
                                      ? 'text-danger'
                                      : 'invisible'
                                  }`}
                                >
                                  {errors.breakStartAt || '.'}
                                </div>
                              </div>
                            ) : (
                              formatDisplayDate(row.breakStartAt)
                            )}
                          </td>
                          <td>
                            {isEditing ? (
                              <div
                                className="d-flex flex-column"
                                style={{ minWidth: '150px', maxWidth: '180px' }}
                              >
                                <Flatpickr
                                  className="form-control"
                                  options={{
                                    enableTime: true,
                                    dateFormat: CalendarFormats.dateTimeFormat,
                                    time_24hr: true,
                                    locale: getLocale(),
                                    minDate: minSelectableDate,
                                    maxDate: maxSelectableDate,
                                  }}
                                  value={row.breakEndAt ?? undefined}
                                  onChange={(date) =>
                                    handleDateChange(
                                      row.id,
                                      'breakEndAt',
                                      date[0]
                                    )
                                  }
                                />
                                <div
                                  className={`mt-1 ${
                                    errors.breakEndAt
                                      ? 'text-danger'
                                      : 'invisible'
                                  }`}
                                >
                                  {errors.breakEndAt || '.'}
                                </div>
                              </div>
                            ) : (
                              formatDisplayDate(row.breakEndAt)
                            )}
                          </td>
                          <td className="text-center">
                            {/* {formatDisplayValue(row.breakingHours)} */}
                            {hoursToHHMM(Number(row.breakingHours || 0))}
                          </td>
                          <td>
                            {isEditing ? (
                              <div
                                className="d-flex flex-column"
                                style={{ minWidth: '150px', maxWidth: '180px' }}
                              >
                                <Input
                                  value={row.note}
                                  onChange={(e) =>
                                    handleChangeField(row.id, e.target.value)
                                  }
                                  maxLength={255}
                                  placeholder={t('Note')}
                                />
                                <div
                                  className={`mt-1 ${
                                    errors.note ? 'text-danger' : 'invisible'
                                  }`}
                                >
                                  {errors.note || '.'}
                                </div>
                              </div>
                            ) : (
                              formatDisplayValue(row.note)
                            )}
                          </td>
                          <td
                            className={`text-center ${isEditing ? 'align-top' : ''}`}
                          >
                            {isEditing ? (
                              <div className="d-flex justify-content-center gap-2">
                                <Button
                                  color="success"
                                  className="btn w-sm material-shadow-none"
                                  onClick={() => handleSaveClick(row.id)}
                                  disabled={isSaving}
                                >
                                  {t('Btn_Save')}
                                </Button>
                                <Button
                                  color="light"
                                  className="btn w-sm material-shadow-none"
                                  onClick={() => handleCancelClick(row.id)}
                                  disabled={isSaving}
                                >
                                  {t('Btn_Cancel')}
                                </Button>
                              </div>
                            ) : (
                              <div className="d-flex justify-content-center">
                                <IconActionButton
                                  onClick={() => handleEditClick(row.id)}
                                  icon={
                                    <FaRegPenToSquare
                                      fontSize={
                                        TimeAttendanceListActionButtons.icon
                                      }
                                    />
                                  }
                                  title={t('Edit')}
                                  height={
                                    TimeAttendanceListActionButtons.button
                                      .height
                                  }
                                  width={
                                    TimeAttendanceListActionButtons.button.width
                                  }
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          </ModalBody>
        </Modal>
      </Container>
    </div>
  );
};

export default BreakingTimeModal;
